import { COMPANY_SIZE_OPTIONS } from "@/shared/utils/contactConfig";
import { z } from "zod";

// email regex
const EMAIL_REGEX =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

// linkedin url regex
const LINKEDIN_URL_REGEX =
  /^https?:\/\/([a-z0-9.-]+\.)?linkedin\.[a-z0-9.]+\/in\/[^/?#\s]+\/?(?:[?#].*)?$/i;

export const isLinkedInCompanyUrl = (value) =>
  /^(?:https?:\/\/)?(?:[a-z0-9-]+\.)*linkedin\.[a-z0-9.]+\/company(?:[/?#]|$)/i.test(
    String(value ?? "").trim(),
  );

// phone number regex
const PHONE_REGEX = /^\+?[\d\s\-().]{7,20}$/;

const trimmedString = (maxLen, fieldName) =>
  z
    .string()
    .transform((val) => val?.trim())
    .pipe(
      z
        .string()
        .min(1, `${fieldName} is required`)
        .max(maxLen, `Must be ${maxLen} characters or less`)
    );

const optionalText = (maxLen, fieldName) =>
  z
    .string()
    .transform((val) => val?.trim() ?? "")
    .pipe(z.string().max(maxLen, `${fieldName} must be ${maxLen} characters or less`))
    .default("");

export const addContactFormSchema = z.object({
  fullName: trimmedString(120, "Full name"),

  designation: trimmedString(100, "Designation"),

  company: trimmedString(150, "Company"),

  companySize: z
  .string({
    required_error: "Company size is required",
    invalid_type_error: "Company size is required",
  })
  .refine(
    (val) =>
      COMPANY_SIZE_OPTIONS.some((option) => option.value === val),
    {
      message: "Please select a valid company size",
    }
  ),

  email: z
    .string()
    .transform((val) => val?.trim().toLowerCase())
    .pipe(
      z
        .string()
        .min(1, "Email is required")
        .max(254, "Email address is too long")
        .refine((val) => EMAIL_REGEX.test(val), {
          message:
            "Please enter a valid email address (e.g. name@company.com)",
        })
    ),

 
  whatsappNumber: z
    .string()
    .transform((val) => val?.trim() ?? "")
    .refine((val) => {
      if (val === "") return true;
      const digitsOnly = val.replace(/\D/g, "");
      return (
        PHONE_REGEX.test(val) &&
        digitsOnly.length >= 7 &&
        digitsOnly.length <= 15
      );
    }, {
      message:
        "Enter a valid phone number (7–15 digits, e.g. +1 234 567 8900)",
    }),

 
  linkedinUrl: z
    .string()
    .transform((val) => {
      const v = val?.trim();
      if (!v) return "";

      if (/^https?:\/\//i.test(v)) return v;
      if (/^(?:[a-z0-9-]+\.)*linkedin\.[a-z0-9.]+\//i.test(v))
        return `https://${v.replace(/^https?:\/\//i, "")}`;

      return `https://linkedin.com/in/${v}`;
    })
    // Optional: LinkedIn steps are skipped for contacts without a profile.
    .superRefine((val, ctx) => {
      if (!val) return;
      if (isLinkedInCompanyUrl(val)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            "Company LinkedIn URLs are not allowed. Enter the contact's personal LinkedIn profile URL.",
        });
      } else if (!LINKEDIN_URL_REGEX.test(val)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            "Enter a valid LinkedIn URL (e.g. linkedin.com/in/username)",
        });
      }
    }),

  industry: z
    .array(z.string().min(1))
    .min(1, "Industry is required")
    .max(1, "Maximum 1 industry allowed"),

  country: z
    .array(z.string().min(1))
    .min(1, "Country is required")
    .max(1, "Maximum 1 country allowed"),

  companyLinkedinUrl: optionalText(500, "Company LinkedIn URL"),

  companyWebsite: optionalText(500, "Company website"),

  leadSource: optionalText(120, "Lead source"),

  socialLeadId: optionalText(255, "Social lead ID"),

  buyingRole: optionalText(120, "Buying role"),

  persona: optionalText(200, "Persona"),

  description: optionalText(2000, "Description"),

  emailOptOut: z.boolean().default(false),

 
  // headline: z
  //   .string()
  //   .transform((val) => val?.trim())
  //   .pipe(
  //     z
  //       .string()
  //       .min(1, "Headline is required")
  //       .max(200, "Headline must be 200 characters or less")
  //   ),


  // tags: z
  //   .array(z.string().min(1).max(50))
  //   .min(1, "At least one tag is required")
  //   .max(10, "Maximum 10 tags allowed"),
});
